/**
 * Branches — `/admin/branches`.
 *
 * A branch is the operational dimension every document carries, so this screen
 * is deliberately conservative about two things.
 *
 * The code is immutable. Legal numbering masks are built from it — SO-JED-…
 * is not a label, it is part of a document's identity — so the create form
 * takes a code and the edit form shows it without an input.
 *
 * A branch is never deleted; it is made inactive. Nothing here offers a
 * delete, because the contract publishes none.
 *
 * Names are bilingual: the Arabic name is optional and falls back to the Latin
 * one, which is why the form asks for both rather than one "name".
 */
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { localizedName } from "@/lib/i18n";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import { createBranch, updateBranch, type Branch, type BranchStatus } from "../api";
import { platformKeys, useBranchDetail, useBranches } from "../queries";

interface FormState {
  code: string;
  name: string;
  name_ar: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country_code: string;
  status: BranchStatus;
}

const EMPTY: FormState = {
  code: "",
  name: "",
  name_ar: "",
  address_line1: "",
  address_line2: "",
  city: "",
  region: "",
  postal_code: "",
  country_code: "SA",
  status: "active",
};

function formFrom(branch: Branch): FormState {
  return {
    code: branch.code,
    name: branch.name,
    name_ar: branch.name_ar ?? "",
    address_line1: branch.address_line1,
    address_line2: branch.address_line2 ?? "",
    city: branch.city,
    region: branch.region ?? "",
    postal_code: branch.postal_code ?? "",
    country_code: branch.country_code,
    status: branch.status,
  };
}

const blankToNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

export interface BranchesPageProps {
  canWrite?: boolean;
}

export function BranchesPage({ canWrite = false }: BranchesPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<BranchStatus | "all">("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const branches = useBranches(statusFilter);
  const editing = useBranchDetail(editingId);

  // The edit form is seeded from the ETag'd read, so the PATCH validates
  // against the exact version the operator was shown.
  const editingBranch = editing.data?.data;
  useEffect(() => {
    if (editingBranch === undefined) return;
    setForm(formFrom(editingBranch));
  }, [editingBranch]);

  const open = creating || editingId !== null;
  const set = (key: keyof FormState, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const close = () => {
    setCreating(false);
    setEditingId(null);
    setForm(EMPTY);
    setError(null);
  };

  const valid =
    form.name.trim() !== "" &&
    form.address_line1.trim() !== "" &&
    form.city.trim() !== "" &&
    form.country_code.trim().length === 2 &&
    (editingId !== null || form.code.trim() !== "");

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (editingId !== null) {
        const etag = editing.data?.etag;
        if (etag === null || etag === undefined) throw new Error("missing etag");
        await updateBranch(
          editingId,
          {
            name: form.name.trim(),
            name_ar: blankToNull(form.name_ar),
            address_line1: form.address_line1.trim(),
            address_line2: blankToNull(form.address_line2),
            city: form.city.trim(),
            region: blankToNull(form.region),
            postal_code: blankToNull(form.postal_code),
            country_code: form.country_code.trim().toUpperCase(),
            status: form.status,
          },
          etag,
        );
      } else {
        await createBranch(
          {
            code: form.code.trim().toUpperCase(),
            name: form.name.trim(),
            name_ar: blankToNull(form.name_ar),
            address_line1: form.address_line1.trim(),
            address_line2: blankToNull(form.address_line2),
            city: form.city.trim(),
            region: blankToNull(form.region),
            postal_code: blankToNull(form.postal_code),
            country_code: form.country_code.trim().toUpperCase(),
          },
          newIdempotencyKey(),
        );
      }
      await queryClient.invalidateQueries({ queryKey: platformKeys.branches.all });
      close();
    } catch {
      setError(t("platform.branches.saveError", "The branch could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  const rows = branches.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("platform.branches.title", "Branches")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "platform.branches.subtitle",
              "The operational dimension every document carries. A branch is retired by making it inactive, never deleted.",
            )}
          </p>
        </div>
        {canWrite ? (
          <Button
            type="button"
            onClick={() => {
              setForm(EMPTY);
              setCreating(true);
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("platform.branches.new", "New branch")}
          </Button>
        ) : null}
      </div>

      <div className="flex items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="branch-status">{t("platform.branches.status", "Status")}</Label>
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as BranchStatus | "all")}
          >
            <SelectTrigger id="branch-status" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filters.all", "All")}</SelectItem>
              <SelectItem value="active">{t("platform.branchStatus.active", "Active")}</SelectItem>
              <SelectItem value="inactive">
                {t("platform.branchStatus.inactive", "Inactive")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {branches.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {t("platform.branches.empty", "No branches match this filter.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-3 text-start">
                      {t("platform.branches.code", "Code")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("platform.branches.name", "Name")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("platform.branches.city", "City")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("platform.branches.status", "Status")}
                    </th>
                    {canWrite ? <th scope="col" className="p-3" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((branch) => (
                    <tr key={branch.id} className="border-b last:border-0">
                      <td className="p-3">
                        <span dir="ltr" className="font-mono text-xs">
                          {branch.code}
                        </span>
                      </td>
                      <td className="p-3">
                        {localizedName(branch.name, branch.name_ar, i18n.language)}
                      </td>
                      <td className="p-3">{branch.city}</td>
                      <td className="p-3">
                        <Badge variant={branch.status === "active" ? "success" : "outline"}>
                          {t(`platform.branchStatus.${branch.status}`, branch.status)}
                        </Badge>
                      </td>
                      {canWrite ? (
                        <td className="p-3 text-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingId(branch.id)}
                          >
                            {t("actions.edit", "Edit")}
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingId === null
                ? t("platform.branches.new", "New branch")
                : t("platform.branches.edit", "Edit branch")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "platform.branches.formHint",
                "The code is fixed once the branch exists — document numbers are built from it.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            {editingId === null ? (
              <Field
                label={t("platform.branches.code", "Code")}
                htmlFor="branch-code"
                hint={t("platform.branches.codeHint", "Uppercase, e.g. RUH. It cannot change.")}
              >
                <Input
                  id="branch-code"
                  dir="ltr"
                  className="font-mono uppercase"
                  value={form.code}
                  onChange={(event) => set("code", event.target.value)}
                />
              </Field>
            ) : (
              <div className="space-y-1.5">
                <Label>{t("platform.branches.code", "Code")}</Label>
                <p dir="ltr" className="rounded-md border bg-muted px-3 py-2 font-mono text-sm">
                  {form.code}
                </p>
              </div>
            )}

            <Field label={t("platform.branches.nameEn", "Name (English)")} htmlFor="branch-name">
              <Input
                id="branch-name"
                value={form.name}
                onChange={(event) => set("name", event.target.value)}
              />
            </Field>

            <Field
              label={t("platform.branches.nameAr", "Name (Arabic)")}
              htmlFor="branch-name-ar"
              hint={t(
                "platform.branches.nameArHint",
                "Optional. Shown to operators working in Arabic.",
              )}
            >
              <Input
                id="branch-name-ar"
                dir="rtl"
                value={form.name_ar}
                onChange={(event) => set("name_ar", event.target.value)}
              />
            </Field>

            <Field label={t("platform.branches.address1", "Address")} htmlFor="branch-address1">
              <Input
                id="branch-address1"
                value={form.address_line1}
                onChange={(event) => set("address_line1", event.target.value)}
              />
            </Field>

            <Field
              label={t("platform.branches.address2", "Address line 2")}
              htmlFor="branch-address2"
            >
              <Input
                id="branch-address2"
                value={form.address_line2}
                onChange={(event) => set("address_line2", event.target.value)}
              />
            </Field>

            <Field label={t("platform.branches.city", "City")} htmlFor="branch-city">
              <Input
                id="branch-city"
                value={form.city}
                onChange={(event) => set("city", event.target.value)}
              />
            </Field>

            <Field label={t("platform.branches.region", "Region")} htmlFor="branch-region">
              <Input
                id="branch-region"
                value={form.region}
                onChange={(event) => set("region", event.target.value)}
              />
            </Field>

            <Field label={t("platform.branches.postal", "Postal code")} htmlFor="branch-postal">
              <Input
                id="branch-postal"
                dir="ltr"
                value={form.postal_code}
                onChange={(event) => set("postal_code", event.target.value)}
              />
            </Field>

            <Field label={t("platform.branches.country", "Country")} htmlFor="branch-country">
              <Input
                id="branch-country"
                dir="ltr"
                maxLength={2}
                className="w-20 font-mono uppercase"
                value={form.country_code}
                onChange={(event) => set("country_code", event.target.value)}
              />
            </Field>

            {editingId === null ? null : (
              <div className="space-y-1.5">
                <Label htmlFor="branch-form-status">
                  {t("platform.branches.status", "Status")}
                </Label>
                <Select
                  value={form.status}
                  onValueChange={(value) => set("status", value as BranchStatus)}
                >
                  <SelectTrigger id="branch-form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      {t("platform.branchStatus.active", "Active")}
                    </SelectItem>
                    <SelectItem value="inactive">
                      {t("platform.branchStatus.inactive", "Inactive")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {error === null ? null : (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              {t("actions.cancel", "Cancel")}
            </Button>
            <Button type="button" disabled={!valid || busy} onClick={() => void save()}>
              {t("actions.save", "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint === undefined ? null : <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
